// 🔧 seo_seeder.js
// ✅ Purpose: SEO Seeder for the LGV Seed Engine
// ✅ Features:
// - Writes SEO fields into the event document
// - Mirrors the same data consumed by SeoHead
// - Future-ready for AI-generated SEO
// - Fully block-commented for clarity and future devs

import {
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  httpsCallable,
} from "firebase/functions";

import { db, functions } from "../../../firebase";
import { logger } from "../../../utils/logger";

import {
  buildSeoSettings,
} from "../../templates/seo_defaults";
import {
  createDefaultEventSeoPages,
  EVENT_SEO_PAGE_KEYS,
  getEventSeoPageKeys,
} from "../../../utils/seo_page_defaults";

const getDemoSeoPageKeys = (addOns = {}) =>
  getEventSeoPageKeys({ purchasedAddOns: addOns }).filter((pageKey) =>
    EVENT_SEO_PAGE_KEYS.includes(pageKey)
  );

const DEMO_SEO_PAGE_CONTEXT = {
  event_home: { label: "Event", topic: "event information and voting" },
  entries: { label: "Entries", topic: "event entries and participants" },
  sponsors: { label: "Sponsors", topic: "event sponsors and community support" },
  vendors: { label: "Vendors", topic: "event vendors and community businesses" },
  tickets: { label: "Tickets", topic: "event tickets and attendance planning" },
  auction: { label: "Silent Auction", topic: "auction items and fundraising" },
  store: { label: "Event Store", topic: "event merchandise and shopping" },
  passport: { label: "Engagement Passport", topic: "event activities and attendee participation" },
  volunteering: { label: "Volunteering", topic: "volunteer opportunities and event support" },
};

/**
 * Builds a truthful local draft when the optional AI research provider is down.
 * A provider outage must never delete an otherwise valid customer demo.
 */
export const buildFallbackDemoSeoDraft = ({
  eventName = "Demo Event",
  industryTemplate = {},
  demoConfig = {},
  pageKey,
} = {}) => {
  const page = DEMO_SEO_PAGE_CONTEXT[pageKey] || DEMO_SEO_PAGE_CONTEXT.event_home;
  const industry = industryTemplate?.label || "event";
  const location = String(demoConfig?.eventLocation || "").trim();
  const locationText = location ? ` in ${location}` : "";
  const title = `${eventName} ${page.label} | Let's Go Vote`.slice(0, 70);
  const description = `Explore ${page.topic} for ${eventName}, a ${industry} demo Experience${locationText} powered by Let's Go Vote.`.slice(0, 180);

  return {
    status: "pending",
    title,
    description,
    keywords: [industry, page.label, "Event", "Voting", "Let's Go Vote"],
    shortSummary: description,
    suggestedOgTitle: title,
    suggestedOgDescription: description,
    sourceFieldsUsed: [
      "event.name",
      "event.eventType",
      "event.eventLocation",
      "demo.industryTemplate",
    ],
    model: "experience_engine_fallback",
    generatedBy: "experience_engine_seed",
    generatedAt: serverTimestamp(),
    generationError: "",
    skippedReason: "",
  };
};

export const seedSeo = async ({
  eventResult,
  industryTemplate,
  demoConfig,
} = {}) => {

  if (!eventResult?.eventId) {
    throw new Error(
      "SEO Seeder requires an event."
    );
  }

  const seo =
    buildSeoSettings({
      eventName:
        eventResult.eventName,

      industryTemplate,
    });

  await setDoc(
    doc(
      db,
      "events",
      eventResult.eventId
    ),
    {
      ...seo,
      seoPages: createDefaultEventSeoPages({
        purchasedAddOns: demoConfig?.addOns || {},
      }),
    },
    {
      merge: true,
    }
  );

  // The same server-owned agent used by the SEO Queue reads bounded event
  // data. It keeps OpenAI credentials and prompt-source selection off clients.
  const generateEventSeoDraft = httpsCallable(
    functions,
    "generateEventSeoDraft"
  );
  const generatedPageKeys = [];
  const fallbackPageKeys = [];

  for (const pageKey of getDemoSeoPageKeys(demoConfig?.addOns)) {
    try {
      await generateEventSeoDraft({
        eventId: eventResult.eventId,
        pageKey,
      });
      generatedPageKeys.push(pageKey);
    } catch (error) {
      await setDoc(
        doc(db, "events", eventResult.eventId),
        {
          seoPages: {
            [pageKey]: buildFallbackDemoSeoDraft({
              eventName: eventResult.eventName,
              industryTemplate,
              demoConfig,
              pageKey,
            }),
          },
        },
        { merge: true }
      );
      fallbackPageKeys.push(pageKey);
      logger.warn("SEO research unavailable; seeded fallback demo draft.", {
        value: { eventId: eventResult.eventId, pageKey },
      });
    }
  }

  logger.log(
    "🌐 SEO Seeder completed.",
    {
      value: {
        eventId:
          eventResult.eventId,
      },
    }
  );

  return {
    ready: true,

    createdCount: generatedPageKeys.length + fallbackPageKeys.length,

    seo,
    generatedPageKeys,
    fallbackPageKeys,
  };
};

export const seedSeoShell =
async ({
  eventResult,
  industryTemplate,
} = {}) => {

  if (!eventResult?.eventId) {
    return {
      ready: false,

      createdCount: 0,
    };
  }

  return seedSeo({
    eventResult,
    industryTemplate,
  });
};
